import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, MapPin, Ruler, Weight, ExternalLink, Trophy, Target, Shield, Activity, Award, Globe, History } from "lucide-react";
import { api } from "../../lib/api";
import { TornEdge } from "../../components/sport/TornEdge";
import { LnhBadge } from "../../components/sport/LnhBadge";
import Flag from "../../components/sport/Flag";
import BullLoader from "../../components/sport/BullLoader";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DARK = "#160732";
const BG_DEEP = "#280F4F";

const STAT_ICONS = {
  // Stats principales
  played:        { label: "Matchs joués",      icon: Calendar, color: "#a78bfa", suffix: "" },
  goals:         { label: "Buts marqués",      icon: Target,   color: SECONDARY, suffix: "" },
  attempts:      { label: "Tirs tentés",       icon: Activity, color: "#a78bfa", suffix: "" },
  efficiency:    { label: "Efficacité",        icon: Trophy,   color: SECONDARY, suffix: "%" },
  assists:       { label: "Passes décisives",  icon: Award,    color: "#10b981", suffix: "" },
  blocks:        { label: "Contres",           icon: Shield,   color: "#3b82f6", suffix: "" },
  turnovers:     { label: "Pertes de balle",   icon: Activity, color: "#94a3b8", suffix: "" },
  two_min:       { label: "2 minutes",         icon: Activity, color: "#f59e0b", suffix: "" },
  ejecteds:      { label: "Exclusions",        icon: Activity, color: "#dc2626", suffix: "" },
  fouls:         { label: "Fautes commises",   icon: Activity, color: "#f59e0b", suffix: "" },
  goals_field:   { label: "Buts (jeu)",        icon: Target,   color: SECONDARY, suffix: "" },
  goals_7m:      { label: "Buts 7 mètres",     icon: Target,   color: SECONDARY, suffix: "" },
  attempts_7m:   { label: "Tirs 7 mètres",     icon: Activity, color: "#a78bfa", suffix: "" },
  provoked_7m:   { label: "7m provoqués",      icon: Award,    color: "#10b981", suffix: "" },
  provoked_two_min: { label: "2 min provoqués", icon: Award,    color: "#10b981", suffix: "" },
  // Gardien
  saves:         { label: "Arrêts",            icon: Shield,   color: "#3b82f6", suffix: "" },
  conceded:      { label: "Buts encaissés",    icon: Activity, color: "#94a3b8", suffix: "" },
  save_pct:      { label: "% Arrêts",          icon: Shield,   color: "#3b82f6", suffix: "%" },
  // Notes LNH
  lnh_avg:       { label: "Note LNH moy.",     icon: Trophy,   color: SECONDARY, suffix: "" },
  lnh_total:     { label: "Note LNH cumul.",   icon: Trophy,   color: SECONDARY, suffix: "" },
  lnh_score:     { label: "Note LNH",          icon: Trophy,   color: SECONDARY, suffix: "" },
  // Anciens noms conservés pour rétrocompat
  shots:         { label: "Tirs",              icon: Activity, color: "#a78bfa", suffix: "" },
  shots_faced:   { label: "Tirs subis",        icon: Activity, color: "#94a3b8", suffix: "" },
  matches:       { label: "Matchs joués",      icon: Calendar, color: "#a78bfa", suffix: "" },
  play_time:     { label: "Temps de jeu",      icon: Activity, color: "#a78bfa", suffix: "" },
  rating:        { label: "Note moyenne",      icon: Trophy,   color: SECONDARY, suffix: "" },
  minutes_2:     { label: "2 minutes",         icon: Activity, color: "#f59e0b", suffix: "" },
  warnings:      { label: "Avertissements",    icon: Activity, color: "#f59e0b", suffix: "" },
  red_cards:     { label: "Cartons rouges",    icon: Activity, color: "#dc2626", suffix: "" },
  blue_cards:    { label: "Cartons bleus",     icon: Activity, color: "#2563eb", suffix: "" },
  seven_meters:  { label: "7 mètres",          icon: Target,   color: SECONDARY, suffix: "" },
};

/** Champs meta des stats à NE PAS afficher comme indicateurs (ce sont des info contexte). */
const STAT_META_KEYS = new Set([
  "season", "season_id", "team", "team_logo", "jersey_number", "group",
]);

/** Ordre d'affichage privilégié — les stats les plus parlantes d'abord. */
const STAT_ORDER = [
  "played", "goals", "attempts", "efficiency",
  "saves", "save_pct", "conceded",
  "assists", "blocks", "two_min", "lnh_avg",
  "goals_7m", "attempts_7m", "provoked_7m", "provoked_two_min",
  "goals_field", "fouls", "turnovers", "ejecteds", "lnh_total",
];

function _orderedStatEntries(stats) {
  const entries = Object.entries(stats || {}).filter(
    ([k, v]) => !STAT_META_KEYS.has(k) && v !== null && v !== undefined && v !== "" && v !== 0,
  );
  // Sort selon STAT_ORDER puis alphabétique
  entries.sort(([a], [b]) => {
    const ia = STAT_ORDER.indexOf(a);
    const ib = STAT_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  return entries;
}

export default function SportPlayerDetail() {
  const { slug } = useParams();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allPlayers, setAllPlayers] = useState([]);

  useEffect(() => {
    setLoading(true);
    api.get(`/sport/player/${slug}`)
      .then((r) => setPlayer(r.data?.player || null))
      .finally(() => setLoading(false));
    api.get("/sport/team-info").then((r) => setAllPlayers(r.data?.info?.players || [])).catch(() => {});
  }, [slug]);

  const idx = allPlayers.findIndex((p) => p.slug === slug);
  const prevPlayer = idx > 0 ? allPlayers[idx - 1] : null;
  const nextPlayer = idx >= 0 && idx < allPlayers.length - 1 ? allPlayers[idx + 1] : null;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-white">
        <div className="text-pink-500 font-black uppercase tracking-widest text-sm">Chargement de la fiche…</div>
      </div>
    );
  }
  if (!player || !player.name) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white text-center">
        <div className="text-2xl font-black uppercase italic" style={{ color: PRIMARY }}>Joueur introuvable</div>
        <Link to="/sport/effectif" className="mt-4 inline-flex items-center gap-2 text-pink-600 font-bold uppercase text-xs tracking-widest"><ArrowLeft size={13}/> Retour à l'effectif</Link>
      </div>
    );
  }

  const stats = player.current_season_stats || {};
  const seasons = player.seasons || [];
  const flagEmoji = player.country_flag_emoji || "";
  const country = player.country || "";
  const age = player.age;
  const bioItems = [
    player.birthdate && { icon: Calendar, label: "Naissance", value: age ? `${player.birthdate} (${age} ans)` : player.birthdate },
    player.birthplace && { icon: MapPin, label: "Lieu", value: player.birthplace },
    player.height_cm && { icon: Ruler, label: "Taille", value: `${player.height_cm} cm` },
    player.weight_kg && { icon: Weight, label: "Poids", value: `${player.weight_kg} kg` },
  ].filter(Boolean);

  return (
    <div className="bg-white">
      {/* HERO with background photo + overlay */}
      <section className="relative pt-24 pb-12 sm:pt-52 sm:pb-24 overflow-hidden min-h-svh sm:min-h-[80vh] hero-landscape-fit">
        {/* BG = blurred enlarged photo */}
        <div className="absolute inset-0 z-0">
          {player.photo_url && (
            <img src={player.photo_url} alt="" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-70"/>
          )}
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(22,7,50,0.95) 0%, ${PRIMARY}cc 50%, ${BG_DARK}f0 100%)` }}/>
          {/* halftone deco */}
          <div className="absolute inset-0 opacity-15 pointer-events-none" style={{
            backgroundImage: `radial-gradient(circle, ${SECONDARY} 1px, transparent 1px)`,
            backgroundSize: "16px 16px",
          }}/>
        </div>

        <div className="relative z-[2] max-w-6xl mx-auto px-4 lg:px-8">
          <Link to="/sport/effectif" className="inline-flex items-center gap-1.5 text-pink-300 hover:text-pink-200 font-black uppercase tracking-widest text-[11px] mb-6 transition">
            <ArrowLeft size={13}/> Retour à l'effectif
          </Link>

          <div className="grid lg:grid-cols-[1fr,2fr] gap-10 items-center">
            {/* Player photo */}
            <div className="relative">
              {player.photo_url ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
                  className="relative"
                >
                  <div className="absolute -inset-6 rounded-[40%] opacity-40 pointer-events-none"
                    style={{ background: `radial-gradient(circle, ${SECONDARY}aa 0%, transparent 60%)`, filter: "blur(40px)" }}/>
                  <div className="relative aspect-[3/4] rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(236,31,124,0.4)] bg-gradient-to-b from-pink-500/30 to-purple-900/30">
                    <img src={player.photo_url} alt={player.name} className="absolute inset-0 w-full h-full object-cover object-top"/>
                  </div>
                  {/* Jersey number floating */}
                  {player.jersey_number != null && (
                    <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full flex items-center justify-center shadow-2xl ring-4 ring-white"
                      style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                      <span className="text-white font-black italic" style={{ fontSize: "3rem" }}>{player.jersey_number}</span>
                    </div>
                  )}
                </motion.div>
              ) : null}
            </div>

            {/* Player info */}
            <div className="text-white">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <div className="text-pink-300 font-black tracking-[0.3em] text-xs uppercase mb-2">
                  {player.position} {player.team && `· ${player.team}`}
                </div>
                <h1 className="font-black uppercase italic leading-[0.9] mb-4"
                  style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)", textShadow: "0 4px 24px rgba(0,0,0,0.4)" }}>
                  {player.name}
                </h1>

                {/* Nationality flag — prominent */}
                {(player.country_code || flagEmoji || country) && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.15 }}
                    data-testid="player-nationality"
                    className="inline-flex items-center gap-3 px-4 py-2.5 rounded-2xl backdrop-blur-sm border border-pink-400/40 bg-white/10 mb-5">
                    <Flag code={player.country_code} emoji={flagEmoji} size={32} label={country}/>
                    <div>
                      <div className="text-pink-300 text-[10px] font-black uppercase tracking-widest">Nationalité</div>
                      <div className="text-white font-black text-base sm:text-lg uppercase">{country || "—"}</div>
                    </div>
                  </motion.div>
                )}
              </motion.div>

              {/* Bio chips */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                className="flex flex-wrap gap-2 mb-5">
                {bioItems.map((b) => (
                  <span key={b.label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 text-white text-xs">
                    <b.icon size={13} className="text-pink-300"/>
                    <span className="font-bold">{b.label}:</span> {b.value}
                  </span>
                ))}
              </motion.div>

              {/* LNH link */}
              <motion.a
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                href={player.lnh_url} target="_blank" rel="noopener noreferrer"
                data-testid="player-lnh-link"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white font-black uppercase text-[11px] tracking-widest transition">
                <ExternalLink size={13}/> Voir sur LNH.fr
              </motion.a>
            </div>
          </div>
        </div>

        <TornEdge position="bottom" color="#ffffff" variant="shred" height={56}/>
      </section>

      {/* STATS */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-pink-600 font-black tracking-[0.3em] text-xs uppercase mb-2">Saison 2025-2026</div>
            <h2 className="font-black text-3xl sm:text-4xl uppercase italic" style={{ color: PRIMARY }}>
              Statistiques <span style={{ color: SECONDARY }}>en cours</span>
            </h2>
            <p className="text-slate-500 text-sm mt-3 inline-flex items-center justify-center gap-1.5 flex-wrap">
              <span>Synchronisé en direct depuis</span> <LnhBadge size="sm"/>
            </p>
          </div>

          {(() => {
            const orderedStats = _orderedStatEntries(stats);
            return orderedStats.length === 0 ? (
              <div className="text-center py-16">
                <div className="inline-block px-6 py-3 rounded-full bg-pink-50 text-pink-700 font-bold mb-4 text-sm">Statistiques en cours de mise à jour</div>
                <p className="text-slate-500 text-sm max-w-xl mx-auto">Les stats détaillées seront disponibles dès que la LNH publiera les performances individuelles. En attendant, retrouvez la fiche complète sur le site officiel.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {orderedStats.map(([k, v], i) => {
                  const meta = STAT_ICONS[k] || { label: k, icon: Activity, color: PRIMARY, suffix: "" };
                  const Icon = meta.icon;
                  return (
                    <motion.div key={k}
                      initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                      className="rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl hover:border-pink-200 transition relative overflow-hidden bg-white"
                      data-testid={`stat-${k}`}>
                      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10" style={{ background: meta.color }}/>
                      <Icon size={20} className="mb-3" style={{ color: meta.color }}/>
                      <div className="text-3xl font-black tabular-nums" style={{ color: PRIMARY }}>
                        {v}{meta.suffix || ""}
                      </div>
                      <div className="text-slate-500 text-[11px] font-black uppercase tracking-widest mt-1">{meta.label}</div>
                    </motion.div>
                  );
                })}
              </div>
            );
          })()}

          {/* LNH attribution + CTA */}
          <div className="mt-10 text-center">
            <a href={player.lnh_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-black uppercase text-xs tracking-widest text-white shadow-lg hover:scale-105 transition"
              style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
              <ExternalLink size={14}/> <span>Fiche complète</span> <LnhBadge size="sm" className="-my-1"/>
            </a>
          </div>
        </div>
      </section>

      {/* CARRIÈRE — saisons LNH */}
      {seasons.length > 0 && (
        <section className="py-16" style={{ background: BG_DEEP }}>
          <div className="max-w-5xl mx-auto px-4 lg:px-8">
            <div className="text-center mb-10">
              <div className="text-pink-400 font-black tracking-[0.3em] text-xs uppercase mb-2 inline-flex items-center gap-2 justify-center">
                <History size={12}/> <span>Parcours</span> <LnhBadge size="sm"/>
              </div>
              <h2 className="text-white font-black text-3xl sm:text-4xl uppercase italic inline-flex items-center justify-center gap-3 flex-wrap">
                {seasons.length} saison{seasons.length > 1 ? "s" : ""} <span style={{ color: SECONDARY }}>en</span> <LnhBadge size="lg"/>
              </h2>
              <p className="text-white/60 text-sm mt-3 max-w-2xl mx-auto inline-flex items-center justify-center gap-1.5 flex-wrap">
                <span>Saisons recensées sur</span> <LnhBadge size="sm"/> <span>— équipe pro & compétitions officielles.</span>
              </p>
            </div>

            <div className="relative pl-10 sm:pl-14" data-testid="player-career">
              {/* vertical line */}
              <div className="absolute left-3 sm:left-5 top-2 bottom-2 w-0.5 rounded-full"
                style={{ background: `linear-gradient(180deg, ${SECONDARY}, ${PRIMARY})` }}/>

              <div className="space-y-4">
                {seasons.map((s, i) => (
                  <motion.div key={s}
                    initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className="relative rounded-2xl bg-white/5 border border-white/10 hover:border-pink-400/40 px-5 py-4 flex items-center gap-4">
                    {/* dot */}
                    <span className="absolute -left-[34px] sm:-left-[42px] top-1/2 -translate-y-1/2 w-5 h-5 rounded-full ring-4 ring-[#280F4F] shadow-lg"
                      style={{ background: i === 0 ? SECONDARY : "white" }}/>
                    <div className="flex-1 flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="text-pink-300 font-black tracking-[0.3em] text-[10px] uppercase">
                          Saison
                        </div>
                        <div className="text-white font-black text-xl sm:text-2xl">{s.replace("/", " / ")}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {i === 0 && (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-white"
                            style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                            En cours
                          </span>
                        )}
                        {player.team && (
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-white text-xs font-bold">
                            {player.team_logo && <img src={player.team_logo} alt="" className="w-5 h-5 rounded-full bg-white p-0.5"/>}
                            {i === 0 ? player.team : "—"}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* career summary card */}
              <motion.div
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: seasons.length * 0.05 + 0.1 }}
                className="mt-6 rounded-2xl p-5 grid grid-cols-2 sm:grid-cols-4 gap-4"
                style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DARK})` }}>
                <div>
                  <div className="text-pink-300 text-[10px] font-black uppercase tracking-widest">Saisons</div>
                  <div className="text-white font-black text-2xl">{seasons.length}</div>
                </div>
                <div>
                  <div className="text-pink-300 text-[10px] font-black uppercase tracking-widest">Première</div>
                  <div className="text-white font-black text-base">{seasons[seasons.length - 1]?.replace("/", "/") || "—"}</div>
                </div>
                <div>
                  <div className="text-pink-300 text-[10px] font-black uppercase tracking-widest">Actuelle</div>
                  <div className="text-white font-black text-base">{seasons[0]?.replace("/", "/") || "—"}</div>
                </div>
                <div>
                  <div className="text-pink-300 text-[10px] font-black uppercase tracking-widest">Position</div>
                  <div className="text-white font-black text-base truncate">{player.position || "—"}</div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* PREV / NEXT NAV */}
      {(prevPlayer || nextPlayer) && (
        <section className="py-12" style={{ background: BG_DEEP }}>
          <div className="max-w-5xl mx-auto px-4 lg:px-8 grid sm:grid-cols-2 gap-4">
            {prevPlayer ? (
              <Link to={`/sport/joueur/${prevPlayer.slug}`}
                className="rounded-2xl p-5 bg-white/5 border border-white/10 hover:border-pink-400 hover:bg-white/10 transition group flex items-center gap-4 text-white">
                <ArrowLeft size={22} className="text-pink-400 group-hover:-translate-x-1 transition shrink-0"/>
                <div className="flex items-center gap-3 min-w-0">
                  {prevPlayer.photo_url && <img src={prevPlayer.photo_url} alt={prevPlayer.name} className="w-12 h-12 rounded-full object-cover object-top shrink-0"/>}
                  <div className="min-w-0">
                    <div className="text-pink-300 text-[10px] font-black uppercase tracking-widest">Précédent</div>
                    <div className="font-black text-base uppercase truncate">{prevPlayer.name}</div>
                  </div>
                </div>
              </Link>
            ) : <div/>}
            {nextPlayer ? (
              <Link to={`/sport/joueur/${nextPlayer.slug}`}
                className="rounded-2xl p-5 bg-white/5 border border-white/10 hover:border-pink-400 hover:bg-white/10 transition group flex items-center gap-4 text-white text-right justify-end sm:flex-row-reverse">
                <ArrowRight size={22} className="text-pink-400 group-hover:translate-x-1 transition shrink-0"/>
                <div className="flex items-center gap-3 min-w-0 sm:flex-row-reverse">
                  {nextPlayer.photo_url && <img src={nextPlayer.photo_url} alt={nextPlayer.name} className="w-12 h-12 rounded-full object-cover object-top shrink-0"/>}
                  <div className="min-w-0">
                    <div className="text-pink-300 text-[10px] font-black uppercase tracking-widest">Suivant</div>
                    <div className="font-black text-base uppercase truncate">{nextPlayer.name}</div>
                  </div>
                </div>
              </Link>
            ) : <div/>}
          </div>
        </section>
      )}
    </div>
  );
}
